import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'

const REVALIDATE_AFTER_MS = 30_000
const MAX_ENTRIES = 20

interface CachedRepositories {
  repositories: AgentDropdownRepository[]
  fetchedAt: number
}

const cache = new Map<string, CachedRepositories>()
const inFlight = new Map<string, Promise<AgentDropdownRepository[]>>()
const lastRequestedAt = new Map<string, number>()
let generation = 0

export function chatTargetRepositoryCacheKey(
  scopeOwner: string | null | undefined,
  query: string,
  includedRepositoryNameWithOwner?: string,
  mode: 'preferred' | 'scoped' = 'scoped',
  sandboxDisabledOwners: readonly string[] = [],
): string {
  return JSON.stringify([
    (scopeOwner ?? '').trim().toLowerCase(),
    query.trim().toLowerCase(),
    (includedRepositoryNameWithOwner ?? '').trim().toLowerCase(),
    ...(mode === 'preferred'
      ? [mode, [...new Set(sandboxDisabledOwners.map(owner => owner.toLowerCase()))].sort()]
      : []),
  ])
}

export function getCachedChatTargetRepositories(key: string): CachedRepositories | undefined {
  return cache.get(key)
}

export function shouldRevalidateChatTargetRepositories(key: string, now = Date.now()): boolean {
  const entry = cache.get(key)
  return !entry || now - entry.fetchedAt >= REVALIDATE_AFTER_MS
}

export function revalidateChatTargetRepositories(
  key: string,
  load: () => Promise<AgentDropdownRepository[]>,
): Promise<AgentDropdownRepository[]> | undefined {
  const pending = inFlight.get(key)
  if (pending) return pending
  const now = Date.now()
  if (!shouldRevalidateChatTargetRepositories(key, now)) return undefined
  const lastRequest = lastRequestedAt.get(key)
  if (cache.has(key) && lastRequest !== undefined && now - lastRequest < REVALIDATE_AFTER_MS) return undefined

  lastRequestedAt.delete(key)
  lastRequestedAt.set(key, now)
  if (lastRequestedAt.size > MAX_ENTRIES) {
    const oldestKey = lastRequestedAt.keys().next().value
    if (oldestKey !== undefined) lastRequestedAt.delete(oldestKey)
  }
  const requestGeneration = generation
  const request = (async () => {
    try {
      await Promise.resolve()
      const repositories = await load()
      if (generation === requestGeneration) cacheChatTargetRepositories(key, repositories)
      return repositories
    } catch (error) {
      if (generation === requestGeneration) lastRequestedAt.delete(key)
      throw error
    } finally {
      if (generation === requestGeneration) inFlight.delete(key)
    }
  })()
  inFlight.set(key, request)
  return request
}

export function cacheChatTargetRepositories(
  key: string,
  repositories: AgentDropdownRepository[],
  fetchedAt = Date.now(),
): void {
  cache.delete(key)
  cache.set(key, {repositories, fetchedAt})
  if (cache.size > MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value
    if (oldestKey !== undefined) cache.delete(oldestKey)
  }
}

export function clearChatTargetRepositoryCache(): void {
  generation++
  cache.clear()
  inFlight.clear()
  lastRequestedAt.clear()
}
